import { PublicKey, TransactionInstruction } from "@solana/web3.js";
import { MerkleClaim } from "../rest/kairos/types";
import { useState } from "react";
import { useContext } from "react";
import { AppConfigContext } from "../context";
import { getAssociatedTokenAddress } from "@solana/spl-token";
import { createAssociatedTokenAccountIdempotentInstruction } from "@solana/spl-token";
import { TOKEN_PROGRAM_ID } from "@solana/spl-token";
import mixpanel from "mixpanel-browser";
import { useAnchorWallet, useConnection, useWallet } from "@solana/wallet-adapter-react";
import { createClaimRewardsPayload } from "../helpers/payloads";
import { sendTx } from "./utils/utils";

export const useAirdropClaim = () => {
    const { apiUrl } = useContext(AppConfigContext);
    const { connection } = useConnection();
    const { sendTransaction } = useWallet();
    const wallet = useAnchorWallet()
    const { environment, priorityFeeLevel, merkyProgramAddress } = useContext(AppConfigContext)
    const merkyProgram = new PublicKey(merkyProgramAddress)
    const [merkleClaim, setMerkleClaim] = useState<MerkleClaim | null>(null);
    const [claimed, setClaimed] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const fetchAirdropClaim = async (wallet: PublicKey | null) => {
        if (!wallet) {
            setError("Invalid parameters");
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            const response = await fetch(`${apiUrl}/airdrop/claim?wallet=${wallet.toBase58()}`);
            const data = await response.json();
            if (data) {
                if (data.error) {
                    setError(data.error);
                    setMerkleClaim(null);
                    return;
                } else {
                    const claimData: MerkleClaim = data;
                    setMerkleClaim(claimData);
                }
            }
        } catch (err) {
            console.error("Error fetching Airdrop Claim:", err);
            setError("Error fetching Airdrop Claim");
            setIsLoading(false);
        }
    };

    const onchainClaimRewards = async (merkleClaim: MerkleClaim) => {
        if (!merkleClaim || !wallet) {
            setError("Invalid parameters");
            return;
        }

        setIsLoading(true);
        setError(null);

        mixpanel.track("Claim Airdrop", {
            "wallet": wallet.publicKey.toBase58(),
        })

        const ixs: TransactionInstruction[] = [];

        const distributorAta = await getAssociatedTokenAddress(new PublicKey(merkleClaim.asset), new PublicKey(merkleClaim.distributor), true);

        const seeds = [
            Buffer.from("ClaimStatus"),
            wallet.publicKey.toBuffer(),
            new PublicKey(merkleClaim.distributor).toBuffer(),
        ];
        const [claimStatus] = PublicKey.findProgramAddressSync(seeds, merkyProgram);
        
        // 1. ATA for Token / wallet
        const destinationAta = await getAssociatedTokenAddress(new PublicKey(merkleClaim.asset), wallet.publicKey, true);

        const createIdempotentTokenAtaIx = createAssociatedTokenAccountIdempotentInstruction(
            wallet.publicKey,
            destinationAta,
            wallet.publicKey,
            new PublicKey(merkleClaim.asset)
        )
        ixs.push(createIdempotentTokenAtaIx);

        // 2. Claim
        const claimIx = new TransactionInstruction({
            keys: [
                { pubkey: new PublicKey(merkleClaim.distributor), isSigner: false, isWritable: true }, // Merkle distributor
                { pubkey: claimStatus, isSigner: false, isWritable: true }, // Claim status
                { pubkey: distributorAta, isSigner: false, isWritable: true }, // Distributor ATA
                { pubkey: destinationAta, isSigner: false, isWritable: true }, // Destination ATA
                { pubkey: wallet.publicKey, isSigner: true, isWritable: true }, // Payer
                { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
                { pubkey: new PublicKey("11111111111111111111111111111111"), isSigner: false, isWritable: false },
            ],
            programId: merkyProgram,
            data: createClaimRewardsPayload(merkleClaim)
        })
        ixs.push(claimIx);

        try {
            const signature = await sendTx(environment, connection, wallet.publicKey, sendTransaction, ixs, priorityFeeLevel);
            setError(null);
            setClaimed(signature);
        } catch (err) {
            setError(`Something went wrong with the transaction: ${err}`);
        } finally {
            setIsLoading(false);
        }
    }

    return { merkleClaim, isLoading, error, fetchAirdropClaim, onchainClaimRewards, claimed };
};